import { useEffect, useRef } from 'react';
import type { MutableRefObject, Dispatch, SetStateAction } from 'react';
import type { AskMessage } from '@/components/common/Ask/Ask';

type AskSessionTypewriterParams = {
  streamingMessageIdRef: MutableRefObject<string | null>;
  toRevealRef: MutableRefObject<string>;
  displayedContentRef: MutableRefObject<string>;
  setMessages: Dispatch<SetStateAction<AskMessage[]>>;
  delayMs: number;
};

export const useAskSessionTypewriterEffect = ({
  streamingMessageIdRef,
  toRevealRef,
  displayedContentRef,
  setMessages,
  delayMs,
}: AskSessionTypewriterParams) => {
  // Throttle state updates to avoid excessive renders while typing.
  // This also helps prevent React "maximum update depth" issues.
  const lastSetMessagesAtRef = useRef<number>(0);

  useEffect(() => {
    const interval = setInterval(() => {
      const messageId = streamingMessageIdRef.current;
      if (!messageId) return;

      const pending = toRevealRef.current;
      if (!pending.length) return;

      const nextSegment = pending.match(/^\s*\S+/s)?.[0] ?? pending;
      if (!nextSegment) return;

      toRevealRef.current = pending.slice(nextSegment.length);
      displayedContentRef.current += nextSegment;
      const content = displayedContentRef.current;

      const now = Date.now();
      const MIN_SET_MESSAGES_INTERVAL_MS = 100;
      if (now - lastSetMessagesAtRef.current < MIN_SET_MESSAGES_INTERVAL_MS) {
        return;
      }
      lastSetMessagesAtRef.current = now;

      setMessages((prev) =>
        prev.map((msg) => (msg.id === messageId ? { ...msg, content } : msg)),
      );
    }, delayMs);

    return () => clearInterval(interval);
  }, [streamingMessageIdRef, toRevealRef, displayedContentRef, setMessages, delayMs]);
};

